// Safari Polyfills for Kivity App
// This file contains polyfills for Safari compatibility issues

declare global {
  interface Window {
    MSInputMethodContext?: any;
    webkitURL?: any;
  }
}

// 1. Array.prototype.find polyfill (Safari < 9)
if (!Array.prototype.find) {
  Array.prototype.find = function<T>(this: T[], predicate: (value: T, index: number, obj: T[]) => boolean): T | undefined {
    if (this == null) {
      throw new TypeError('Array.prototype.find called on null or undefined');
    }
    if (typeof predicate !== 'function') {
      throw new TypeError('predicate must be a function');
    }
    const list = Object(this);
    const length = parseInt(list.length) || 0;
    for (let i = 0; i < length; i++) {
      if (predicate.call(arguments[1], list[i], i, list)) {
        return list[i];
      }
    }
    return undefined;
  };
}

// 2. Array.prototype.findIndex polyfill (Safari < 9)
if (!Array.prototype.findIndex) {
  Array.prototype.findIndex = function<T>(this: T[], predicate: (value: T, index: number, obj: T[]) => boolean): number {
    if (this == null) {
      throw new TypeError('Array.prototype.findIndex called on null or undefined');
    }
    if (typeof predicate !== 'function') {
      throw new TypeError('predicate must be a function');
    }
    const list = Object(this);
    const length = parseInt(list.length) || 0;
    for (let i = 0; i < length; i++) {
      if (predicate.call(arguments[1], list[i], i, list)) {
        return i;
      }
    }
    return -1;
  };
}

// 3. Array.prototype.includes polyfill (Safari < 9)
if (!Array.prototype.includes) {
  Array.prototype.includes = function<T>(this: T[], searchElement: T, fromIndex?: number): boolean {
    'use strict';
    if (this == null) {
      throw new TypeError('Array.prototype.includes called on null or undefined');
    }
    
    const O = Object(this);
    const len = parseInt(O.length) || 0;
    if (len === 0) return false;
    
    const n = parseInt(fromIndex as any) || 0;
    let k = Math.max(n >= 0 ? n : len - Math.abs(n), 0);
    
    function sameValueZero(x: any, y: any) {
      return x === y || (typeof x === 'number' && typeof y === 'number' && isNaN(x) && isNaN(y));
    }
    
    while (k < len) {
      if (sameValueZero(O[k], searchElement)) {
        return true;
      }
      k++;
    }
    return false;
  };
}

// 4. String.prototype.includes polyfill (Safari < 9)
if (!String.prototype.includes) {
  String.prototype.includes = function(search: string, start?: number): boolean {
    'use strict';
    if (typeof start !== 'number') {
      start = 0;
    }
    if (start + search.length > this.length) {
      return false;
    } else {
      return this.indexOf(search, start) !== -1;
    }
  };
}

// 5. String.prototype.startsWith polyfill (Safari < 9)
if (!String.prototype.startsWith) {
  String.prototype.startsWith = function(searchString: string, position?: number): boolean {
    position = position || 0;
    return this.substr(position, searchString.length) === searchString;
  };
}

// 6. String.prototype.endsWith polyfill (Safari < 9)
if (!String.prototype.endsWith) {
  String.prototype.endsWith = function(searchString: string, length?: number): boolean {
    if (length === undefined || length > this.length) {
      length = this.length;
    }
    return this.substring(length - searchString.length, length) === searchString;
  };
}

// 7. Object.assign polyfill (Safari < 9)
if (typeof Object.assign !== 'function') {
  Object.assign = function(target: any) {
    if (target == null) {
      throw new TypeError('Cannot convert undefined or null to object');
    }
    
    const to = Object(target);
    
    for (let index = 1; index < arguments.length; index++) {
      const nextSource = arguments[index];
      
      if (nextSource != null) {
        for (const nextKey in nextSource) {
          if (Object.prototype.hasOwnProperty.call(nextSource, nextKey)) {
            to[nextKey] = nextSource[nextKey];
          }
        }
      }
    }
    return to;
  };
}

// 8. Promise polyfill (Safari < 7.1)
if (typeof Promise === 'undefined') {
  // Simple Promise polyfill
  (window as any).Promise = class {
    private state: 'pending' | 'fulfilled' | 'rejected' = 'pending';
    private value: any;
    private handlers: Array<{ onFulfilled?: Function; onRejected?: Function; resolve: Function; reject: Function }> = [];

    constructor(executor: (resolve: (value?: any) => void, reject: (reason?: any) => void) => void) {
      try {
        executor(this.resolve.bind(this), this.reject.bind(this));
      } catch (e) {
        this.reject(e);
      }
    }

    private resolve(value: any) {
      if (this.state === 'pending') {
        this.state = 'fulfilled';
        this.value = value;
        this.handlers.forEach(this.handle.bind(this));
        this.handlers = [];
      }
    }

    private reject(reason: any) {
      if (this.state === 'pending') {
        this.state = 'rejected';
        this.value = reason;
        this.handlers.forEach(this.handle.bind(this));
        this.handlers = [];
      }
    }

    private handle(handler: any) {
      if (this.state === 'pending') {
        this.handlers.push(handler);
      } else {
        if (this.state === 'fulfilled' && typeof handler.onFulfilled === 'function') {
          handler.onFulfilled(this.value);
        }
        if (this.state === 'rejected' && typeof handler.onRejected === 'function') {
          handler.onRejected(this.value);
        }
      }
    }

    then(onFulfilled?: Function, onRejected?: Function) {
      return new (this.constructor as any)((resolve: Function, reject: Function) => {
        this.handle({
          onFulfilled: onFulfilled,
          onRejected: onRejected,
          resolve: resolve,
          reject: reject
        });
      });
    }

    catch(onRejected: Function) {
      return this.then(undefined, onRejected);
    }

    static resolve(value: any) {
      return new this((resolve: Function) => resolve(value));
    }

    static reject(reason: any) {
      return new this((resolve: Function, reject: Function) => reject(reason));
    }
  };
}

// 9. Fetch API polyfill (Safari < 10.1)
if (typeof fetch === 'undefined') {
  (window as any).fetch = function(url: string, options: any = {}) {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      const method = options.method || 'GET';
      
      xhr.open(method, url);
      
      // Set headers
      if (options.headers) {
        Object.keys(options.headers).forEach(key => {
          xhr.setRequestHeader(key, options.headers[key]);
        });
      }
      
      xhr.onload = () => {
        const response = {
          ok: xhr.status >= 200 && xhr.status < 300,
          status: xhr.status,
          statusText: xhr.statusText,
          headers: new Map(),
          json: () => Promise.resolve(JSON.parse(xhr.responseText)),
          text: () => Promise.resolve(xhr.responseText)
        };
        resolve(response);
      };
      
      xhr.onerror = () => reject(new Error('Network Error'));
      xhr.ontimeout = () => reject(new Error('Request Timeout'));
      
      xhr.send(options.body || null);
    });
  };
}

// 10. URL polyfill (Safari < 10)
if (typeof URL === 'undefined' && typeof window.webkitURL !== 'undefined') {
  (window as any).URL = window.webkitURL;
}

// 11. requestAnimationFrame polyfill
if (!window.requestAnimationFrame) {
  window.requestAnimationFrame = function(callback: FrameRequestCallback): number {
    return setTimeout(callback, 1000 / 60);
  };
}

if (!window.cancelAnimationFrame) {
  window.cancelAnimationFrame = function(id: number): void {
    clearTimeout(id);
  };
}

// 12. Console polyfill (very old Safari)
if (typeof console === 'undefined') {
  (window as any).console = {
    log: function() {},
    error: function() {},
    warn: function() {},
    info: function() {},
    debug: function() {}
  };
}

// 13. Element.closest polyfill (Safari < 6)
if (!Element.prototype.closest) {
  Element.prototype.closest = function(selector: string): Element | null {
    let element = this as Element;
    while (element && element.nodeType === 1) {
      if (element.matches && element.matches(selector)) {
        return element;
      }
      element = element.parentElement!;
    }
    return null;
  };
}

// 14. Element.matches polyfill
if (!Element.prototype.matches) {
  Element.prototype.matches = function(selector: string): boolean {
    const element = this;
    const doc = element.ownerDocument || document;
    const matches = doc.querySelectorAll(selector);
    let i = matches.length;
    while (--i >= 0 && matches.item(i) !== element) {}
    return i > -1;
  };
}

// 15. Date.now polyfill (Safari < 5)
if (!Date.now) {
  Date.now = function(): number {
    return new Date().getTime();
  };
}

// 16. Performance.now polyfill
if (!window.performance) {
  (window as any).performance = {};
}

if (!window.performance.now) {
  const startTime = Date.now();
  window.performance.now = function(): number {
    return Date.now() - startTime;
  };
}

// 17. CustomEvent polyfill (Safari < 6)
if (typeof CustomEvent === 'undefined') {
  const CustomEventPolyfill = function(event: string, params: any = {}) {
    const evt = document.createEvent('CustomEvent');
    evt.initCustomEvent(event, params.bubbles || false, params.cancelable || false, params.detail || null);
    return evt;
  };
  (window as any).CustomEvent = CustomEventPolyfill;
}

// 18. IntersectionObserver polyfill fallback
if (typeof IntersectionObserver === 'undefined') {
  (window as any).IntersectionObserver = class {
    constructor(callback: Function, options: any = {}) {
      // Fallback implementation
      this.callback = callback;
      this.options = options;
    }
    
    observe(target: Element) {
      // Simple fallback - just call the callback
      setTimeout(() => {
        this.callback([{
          target,
          isIntersecting: true,
          intersectionRatio: 1,
          boundingClientRect: target.getBoundingClientRect(),
          intersectionRect: target.getBoundingClientRect(),
          rootBounds: null,
          time: Date.now()
        }]);
      }, 100);
    }
    
    unobserve(target: Element) {}
    disconnect() {}
    
    private callback: Function;
    private options: any;
  };
}

// 19. Safari-specific fixes
export const applySafariFixes = () => {
  // Fix Safari date parsing
  const originalDateParse = Date.parse;
  Date.parse = function(dateString: string): number {
    // Convert DD/MM/YYYY to MM/DD/YYYY for Safari
    if (typeof dateString === 'string' && dateString.match(/^\d{1,2}\/\d{1,2}\/\d{4}$/)) {
      const parts = dateString.split('/');
      if (parts.length === 3) {
        // Assume DD/MM/YYYY and convert to MM/DD/YYYY
        dateString = `${parts[1]}/${parts[0]}/${parts[2]}`;
      }
    }
    
    return originalDateParse.call(this, dateString);
  };

  // Fix Safari viewport height issues
  const setViewportHeight = () => {
    const vh = window.innerHeight * 0.01;
    document.documentElement.style.setProperty('--vh', `${vh}px`);
  };
  
  setViewportHeight();
  window.addEventListener('resize', setViewportHeight);
  window.addEventListener('orientationchange', () => {
    setTimeout(setViewportHeight, 500);
  });

  // Fix Safari touch events
  document.addEventListener('touchstart', function() {}, { passive: true });

  // Fix Safari back/forward cache issues
  window.addEventListener('pageshow', function(event) {
    if (event.persisted) {
      window.location.reload();
    }
  });

  // Fix Safari memory issues
  if (typeof (window.performance as any).memory !== 'undefined') {
    const checkMemory = () => {
      const memory = (window.performance as any).memory;
      if (memory && memory.usedJSHeapSize > memory.jsHeapSizeLimit * 0.9) {
        console.warn('[Safari] High memory usage detected, consider refreshing');
      }
    };
    
    setInterval(checkMemory, 30000); // Check every 30 seconds
  }

  console.log('[Safari] Polyfills and fixes applied successfully');
};

// 20. Initialize polyfills
export const initSafariPolyfills = () => {
  try {
    applySafariFixes();
    
    // Test if polyfills are working
    const testArray = [1, 2, 3];
    const testString = 'hello world';

    if (testArray.includes && testArray.includes(2) &&
        testString.includes && testString.includes('world') &&
        typeof Promise !== 'undefined') {
      console.log('[Safari] All polyfills loaded successfully');
      return true;
    } else {
      console.error('[Safari] Some polyfills failed to load');
      return false;
    }
  } catch (error) {
    console.error('[Safari] Error initializing polyfills:', error);
    return false;
  }
};

export default {
  applySafariFixes,
  initSafariPolyfills
}; 